import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react";
import { ThemeProvider } from "@mui/material/styles";

import Router from "navigation";

import GlobalPopup from "services/popup";
import GlobalSnackbarContext from "services/snackbar/snackbarContext";

import Snackbar, {
  SnackbarType,
  SnackbarTypeValues,
} from "components/Snackbar/Snackbar";

import project from "../../package.json";
import { environmentName } from "utils/env";

import { DEFAULT_THEME } from "constants/muiTheme";

import { Version } from "./styles";
import ProfileStore from "stores/ProfileStore";
import AuthStore from "stores/AuthStore";

const App = observer(() => {
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarType, setSnackbarType] = useState<SnackbarType>(
    SnackbarTypeValues.INFO,
  );

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const setMessage = useCallback(
    (message: string, type: SnackbarType = SnackbarTypeValues.INFO) => {
      setSnackbarMessage(message);
      setSnackbarType(type);
    },
    [],
  );

  const onSnackbarClose = useCallback(() => {
    setSnackbarMessage("");
    setSnackbarType(SnackbarTypeValues.INFO);
  }, []);

  const getUTC = () => {
    const now = new Date();
    const offset = -now.getTimezoneOffset();
    const hours = Math.trunc(offset / 60);
    const minutes = offset % 60;

    const sign = offset >= 0 ? "+" : "-";
    const paddedHours = Math.abs(hours).toString().padStart(2, "0");
    const paddedMinutes = Math.abs(minutes).toString().padStart(2, "0");

    return `${sign}${paddedHours}${paddedMinutes}`;
  };

  const checkTimezone = useCallback(async () => {
    const timeZoneFromDevice = getUTC();

    if (
      AuthStore.isAuthorized &&
      ProfileStore.timezone?.id !== timeZoneFromDevice
    ) {
      try {
        await ProfileStore.updateTimeZone(timeZoneFromDevice);
        /** reload the current page to reflect the changes */
        window.location.reload();
      } catch (err) {
        console.log("Error in checkTimezone", err);
      }
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible") {
      checkTimezone();
    }
  }, [checkTimezone]);

  useEffect(() => {
    window.addEventListener("focus", checkTimezone);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    timer.current = setInterval(checkTimezone, 5000);

    return () => {
      if (timer.current) clearInterval(timer.current);
      window.removeEventListener("focus", checkTimezone);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkTimezone, handleVisibilityChange]);

  return (
    <>
      <ThemeProvider theme={DEFAULT_THEME}>
        <GlobalSnackbarContext.Provider value={{ setMessage }}>
          <Router />
          <GlobalPopup />
          <Version>{project.version + "\n" + environmentName}</Version>
        </GlobalSnackbarContext.Provider>
        <Snackbar
          message={snackbarMessage}
          onClose={onSnackbarClose}
          type={snackbarType}
        />
      </ThemeProvider>
    </>
  );
});

export default App;
